import {
  addHighlightCard,
  clearContinueListsAction,
  clearFavoriteListsAction,
  clearViewData,
  clearViewDataById,
  clearViewDataList,
} from './actions';
import { createReducer } from '@reduxjs/toolkit';
import { isList, isNavigator } from '../../client/typeGuards';
import { Card, List } from '@yleisradio/areena-types';
import { getIsGeoBlocked, getRegion } from '../../client/utils/geoBlock';
import { CardList, ListElement } from '../../client/View/viewTypes';
import { ReducerWithInitialState } from '@reduxjs/toolkit/src/createReducer';
import { parseAPICardHero } from '../../client/View/parseAPICardHero';
import { parseAPICard } from '../../client/View/parseAPICard';
import { loadCardPage } from './thunks';
import { isCardWithPointer, ViewData } from './types';
import { pageSize } from './constants';
import { getLocalStorageValue } from '../../client/utils/localStorage';
import { REGIONAL_PREFS_STORAGE_KEY } from '../../client/hooks/useRegionalPrefs';
import { getPreferredCard, getRefreshAt } from './utils';
import { getDataAttributesImpression } from '../../client/analytics/getDataAttributesImpression';
import { isHeroList } from '../../client/utils/list';

const initialState: ViewData = {};

export const viewDataReducer: ReducerWithInitialState<ViewData> = createReducer(
  initialState,
  builder =>
    builder
      .addCase(clearViewData, () => initialState)
      .addCase(clearViewDataById, (state, { payload: viewId }) => {
        if (!state[viewId]) return;

        return Object.entries(state).reduce(
          (acc, [viewIdCurrent, viewData]) => {
            if (viewIdCurrent === viewId) return acc;

            return {
              ...acc,
              [viewIdCurrent]: viewData,
            };
          },
          {}
        );
      })
      .addCase(
        clearViewDataList,
        (state, { payload: { viewId, tabIndex, listIndex } }) => {
          const tab = state[viewId]?.tabs[tabIndex];

          if (!tab) return;

          tab.lists = Object.entries(tab.lists).reduce(
            (acc, [currentListIndex, list]) => {
              if (Number(currentListIndex) === listIndex) {
                return {
                  ...acc,
                  [Number(currentListIndex)]: {
                    ...list,
                    cards: Array.from({ length: pageSize }, (_, index) => ({
                      listIndex,
                      cardIndex: index,
                      data: null,
                    })),
                  },
                };
              } else {
                return {
                  ...acc,
                  [Number(currentListIndex)]: list,
                };
              }
            },
            {}
          );
        }
      )
      .addCase(loadCardPage.pending, (state, action) => {
        const {
          viewId,
          tabIndex: currentTabIndex,
          listIndex,
          pageIndex,
          view,
        } = action.meta.arg;

        const firstList = view?.tabs?.[currentTabIndex]?.content?.[0];
        const viewHasHero = isList(firstList) && isHeroList(firstList.style);

        if (!state[viewId]) {
          state[viewId] = {
            hasHero: viewHasHero,
            tabs: {},
          };
        }

        const viewData = state[viewId];
        if (!viewData) return;

        Object.keys(view?.tabs ?? {}).forEach(tabIndexStr => {
          const tabIndex = Number(tabIndexStr);

          if (isNaN(tabIndex) || viewData.tabs[tabIndex]) return;

          viewData.tabs[tabIndex] = {
            lists: {},
            slug: view?.tabs?.[tabIndex]?.slug,
            title: view?.tabs?.[tabIndex]?.title ?? '',
            heroControls: {},
          };
        });

        const viewTab = viewData.tabs[currentTabIndex];
        if (!viewTab) return;

        const content = view?.tabs?.[currentTabIndex]?.content?.[listIndex];
        const source = isList(content) ? content?.source : undefined;

        if (!viewTab.lists[listIndex]) {
          viewTab.lists[listIndex] = {
            listType: content?.type,
            title: undefined,
            count: undefined,
            cards: [],
            isContinueList: Boolean(source?.uri.includes('continue')),
            isFavoriteList: Boolean(source?.uri.includes('favorites')),
            listApiIndex: listIndex,
            notifications: [],
            style: isList(content) ? content?.style : undefined,
          };
        }

        const cardOffset = pageIndex * pageSize;
        const cardList = viewTab?.lists?.[listIndex];

        if (
          !cardList ||
          (viewHasHero && listIndex === 0) ||
          (cardList.count !== undefined && cardList.count <= cardOffset) ||
          // only populate the list data for non-existing elements to avoid flickering
          cardList.cards.slice(cardOffset, cardOffset + pageSize).length
        ) {
          return;
        }

        cardList.cards.splice(
          cardOffset,
          pageSize,
          ...Array.from({ length: pageSize }, (_, index) => ({
            cardApiIndex: cardOffset + index,
            data: null,
          }))
        );
      })
      .addCase(loadCardPage.fulfilled, (state, { payload, meta }) => {
        const { viewId, tabIndex, listIndex, pageIndex, view } = meta.arg;

        const viewTab = state[viewId]?.tabs[tabIndex];
        const cardList = viewTab?.lists[listIndex];

        if (!viewTab || !cardList) return;

        const now = Date.now();

        cardList.count = payload.meta?.count;
        const listRefreshAt = getRefreshAt(
          payload.meta?.refreshAt,
          payload.data,
          now
        );

        if (listRefreshAt) {
          cardList.refreshAt = listRefreshAt;
        }

        cardList.analytics = payload.meta?.analytics;

        cardList.notifications = payload.notifications ?? [];

        const cardOffset = pageIndex * pageSize;

        const maybePlaceholderIndex = cardList.cards?.findIndex(
          card => card.cardApiIndex === cardOffset
        );
        const firstPlaceholderIndex =
          maybePlaceholderIndex >= 0 ? maybePlaceholderIndex : cardOffset;

        const newCards: ListElement[] = [];

        const regionalPreferences =
          getLocalStorageValue<Record<string, string>>(
            REGIONAL_PREFS_STORAGE_KEY
          ) ?? {};

        payload.data?.forEach((card: Card, cardIndex: number) => {
          const preferredCard = getPreferredCard(card, regionalPreferences);

          const cardTrueIndex = firstPlaceholderIndex + cardIndex;
          const isGeoBlocked = getIsGeoBlocked(
            payload.geoLocation,
            getRegion(preferredCard?.labels ?? [])
          );
          const isBlocked =
            payload.isHbbtvMode &&
            preferredCard.presentation === 'broadcastCard';

          const comscore = payload.meta?.analytics?.context?.comscore;

          const parsedCard = isCardWithPointer(preferredCard)
            ? parseAPICard({
                card: preferredCard,
                cardList,
                listIndex,
                cardIndex: cardTrueIndex,
                comscore,
                isGeoBlocked,
                isBlocked,
              })
            : null;

          if (isHeroList(cardList.style) && listIndex === 0) {
            viewTab.heroControls = parseAPICardHero(
              preferredCard,
              isGeoBlocked,
              payload.languageCode,
              isBlocked,
              parsedCard?.data.dataAttributesImpression ?? null
            );
          }

          if (parsedCard) {
            newCards.push(parsedCard);
          }
        });

        if (newCards.length || payload.notifications) {
          // title should have something so loading-animation doesn't kick in
          const list = view?.tabs?.[tabIndex]?.content?.[listIndex] as List;
          cardList.title = list.title ?? ' ';
        }

        cardList.cards?.splice(firstPlaceholderIndex, pageSize, ...newCards);
      })
      .addCase(
        loadCardPage.rejected,
        (
          state,
          {
            meta: {
              arg: { viewId, tabIndex, listIndex, pageIndex },
            },
          }
        ) => {
          const oldLists = state[viewId]?.tabs[tabIndex]?.lists;

          if (!oldLists) {
            return state;
          }

          const newLists: { [listIndex: number]: CardList } = Object.entries(
            oldLists
          ).reduce((lists, [listIndexCurrentStr, value]) => {
            const listIndexCurrent = Number(listIndexCurrentStr);

            if (listIndexCurrent === listIndex) {
              return {
                ...lists,
                [listIndexCurrent]: removeCardsFromPage(value, pageIndex),
              };
            }

            return {
              ...lists,
              [listIndexCurrent]: value,
            };
          }, {});

          return {
            ...state,
            ...(state[viewId]
              ? {
                  [viewId]: {
                    ...state[viewId],
                    tabs: {
                      ...state[viewId].tabs,
                      ...(state[viewId].tabs[tabIndex]
                        ? {
                            [tabIndex]: {
                              ...state[viewId].tabs[tabIndex],
                              lists: newLists,
                            },
                          }
                        : {}),
                    },
                  },
                }
              : {}),
          };
        }
      )
      .addCase(
        addHighlightCard,
        (
          state,
          { payload: { viewId, tabIndex, listIndex, highlightCard } }
        ) => {
          const lists = state[viewId]?.tabs[tabIndex]?.lists;

          if (lists && !lists?.[listIndex]) {
            lists[listIndex] = {
              cards: [],
              listApiIndex: listIndex,
              listType: 'highlightCard',
              title: undefined,
              highlightCardData: {
                card: highlightCard,
                impressionAttributes: getDataAttributesImpression({
                  listIndex,
                  cardIndex: 0,
                  pointer:
                    highlightCard.controls?.find(isNavigator)?.destination,
                  comscore: highlightCard.analytics?.context?.comscore,
                }),
              },
              count: 1,
            };
          }
        }
      )
      .addCase(clearContinueListsAction, state => {
        return Object.entries(state).reduce(
          (accViews, [viewId, viewContent]) => {
            const newTabs = Object.entries(viewContent.tabs).reduce(
              (accTabs, [tabIndex, tab]) => {
                const newLists = Object.entries(tab.lists).reduce(
                  (accLists, [listIndex, list]) => {
                    if (list.isContinueList)
                      return {
                        ...accLists,
                        [listIndex]: {
                          ...list,
                          cards: [],
                        },
                      };

                    return {
                      ...accLists,
                      [listIndex]: list,
                    };
                  },
                  {}
                );

                return {
                  ...accTabs,
                  [tabIndex]: {
                    ...tab,
                    lists: newLists,
                  },
                };
              },
              {}
            );

            return {
              ...accViews,
              [viewId]: {
                ...viewContent,
                tabs: newTabs,
              },
            };
          },
          {}
        );
      })
      .addCase(clearFavoriteListsAction, state => {
        return Object.entries(state).reduce(
          (accViews, [viewId, viewContent]) => {
            const newTabs = Object.entries(viewContent.tabs).reduce(
              (accTabs, [tabIndex, tab]) => {
                const newLists = Object.entries(tab.lists).reduce(
                  (accLists, [listIndex, list]) => {
                    if (list.isFavoriteList) return accLists;

                    return {
                      ...accLists,
                      [listIndex]: list,
                    };
                  },
                  {}
                );

                return {
                  ...accTabs,
                  [tabIndex]: {
                    ...tab,
                    lists: newLists,
                  },
                };
              },
              {}
            );

            return {
              ...accViews,
              [viewId]: {
                ...viewContent,
                tabs: newTabs,
              },
            };
          },
          {}
        );
      })
);

function removeCardsFromPage(cardList: CardList, pageIndex: number): CardList {
  if (!cardList.cards.length) {
    return cardList;
  }

  const newCards = cardList.cards.slice();

  newCards.splice(pageIndex * pageSize, pageSize);

  return {
    ...cardList,
    cards: newCards,
  };
}
